import { installApplePaySessionGuard, isApplePaySessionFailure } from '@/lib/apple-pay-session-guard';
import { usePersistedStore, useEphemeralStore, DEFAULT_DONOR_COUNTRY } from '@/lib/app-store';
import { createFileRoute, isNotFound } from '@tanstack/react-router';
import { DonateStepPending } from './-components/donate-step-pending';
import { useEffect, useRef, useState } from 'react';
import {
	type DropinConfiguration,
	AdyenCheckout,
	Dropin,
	Card,
	GooglePay,
	ApplePay,
	CashAppPay,
	type PaymentMethodsResponse,
	type ResultCode,
	type Order,
	type PaymentAction,
} from '@adyen/adyen-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { errorMessages } from '@/lib/portal-messages';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { toast } from '@/lib/toast';
import { logErrorFn, extractErrorInfo } from '@/lib/data';
import {
	isApplePaySessionAbortError,
	isClientFaultRpcError,
	isRejectedPaymentDetailsRpcError,
	setApplePayReporterMounted,
} from '@/lib/frontend-error-filter';
import { isVersionSkewReloadInProgress } from '../-components/default-catch-boundary';
import { trpc, trpcClient } from '@/lib/trpc';
import { getRuntimeAdyenClientKey } from '@/lib/shared';
import { Title } from '@worldcoo/ui/components/form/title';
import { Button } from '@worldcoo/ui/components/button';
import { CircleAlertIcon } from 'lucide-react';
import { DivWithPrimaryColorVariable } from './-components/div-with-primary-color-variable';
import { useServerFn } from '@tanstack/react-start';
import { generateCaptchaTokenResult } from '@/lib/captcha';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { amountToMinorUnits, hasMinorUnits } from '@worldcoo/shared/currencies';
import { localeToLanguage } from '@worldcoo/shared/core';
import { retry } from '@worldcoo/shared/utils';
import { areDetailsComplete } from '@/lib/details-schema';
import adyenCssUrl from '@adyen/adyen-web/styles/adyen.css?url';

const ADYEN_LOCALE_BY_LANGUAGE = new Map([
	['es', 'es-ES'],
	['ca', 'ca-ES'],
	['it', 'it-IT'],
]);
const DEFAULT_ADYEN_LOCALE = 'en-US';

const ENABLED_GOOGLE_PAY_PORTALS = new Set(['glovo', 'telefonica', 'rodilla', 'acciona-org', 'intress', 'angelman']);

const ADYEN_HOVER_PRELOADS: Array<{ rel: string; href: string; as?: string }> = [
	{ rel: 'preconnect', href: 'https://checkoutshopper-live.adyen.com' },
	{ rel: 'dns-prefetch', href: 'https://checkoutshopper-live.adyen.com' },
	{ rel: 'preload', href: adyenCssUrl, as: 'style' },
];

// Intent preload fetches only the chunk and head() links land on match, so the loader warms Adyen on hover.
function injectAdyenHoverPreloads() {
	if (typeof document === 'undefined') return;
	for (const { rel, href, as } of ADYEN_HOVER_PRELOADS) {
		if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) continue;
		const link = document.createElement('link');
		link.rel = rel;
		link.href = href;
		if (as) link.as = as;
		document.head.appendChild(link);
	}
}

// The three stages a donation goes through, end to end: docs/notes/portals/donation-process.md
export const Route = createFileRoute('/$portalId/$campaignId/donate/checkout')({
	loader: () => {
		injectAdyenHoverPreloads();
	},
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex' }],
		links: [...ADYEN_HOVER_PRELOADS, { rel: 'stylesheet', href: adyenCssUrl }],
	}),
	component: RouteComponent,
	pendingComponent: DonateStepPending,
});

function RouteComponent() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const hasHydrated = usePersistedStore((state) => state._hasHydrated);
	const amount = usePersistedStore((state) => state.amount);
	const details = usePersistedStore((state) => state.details);
	const { t } = useLingui();
	const { language } = useTranslation();
	const { mutateAsync: preregisterDonation } = useMutation(trpc.payments.preregisterDonation.mutationOptions());
	const { mutateAsync: getPaymentDetails } = useMutation(trpc.payments.getPaymentDetails.mutationOptions());
	const { mutateAsync: processPayment } = useMutation(trpc.payments.processPayment.mutationOptions());
	const dropinContainerRef = useRef<HTMLDivElement>(null);
	const termsContent = usePersistedStore((state) => state.termsContent);
	const ngosTermsContent = usePersistedStore((state) => state.ngosTermsContent);
	const telefonicaTermsContents = usePersistedStore((state) => state.telefonicaTermsContent);
	const setDonationToConfirm = useEphemeralStore((store) => store.setDonationToConfirm);
	const getCachedPreregistration = useEphemeralStore((state) => state.getCachedPreregistration);
	const getCachedPaymentMethods = useEphemeralStore((state) => state.getCachedPaymentMethods);
	const clearCheckoutCache = useEphemeralStore((state) => state.clearCheckoutCache);
	const logError = useServerFn(logErrorFn);

	// A delivered failed final (DR-15): our panel replaces Adyen's, which can say "undefined" and has no retry.
	const [paymentFailed, setPaymentFailed] = useState(false);

	// Undefined for a currency the PSP cannot be charged in: the payment server refuses it too, so stop here.
	const campaign = usePortalCampaignQuery(campaignId);
	const campaignCurrency = isNotFound(campaign) ? undefined : campaign.data.currencyCode;
	const currencyCode = campaignCurrency && hasMinorUnits(campaignCurrency) ? campaignCurrency : undefined;

	// A donor back on this step without pressing Next (back/forward, reload) brings an unvalidated draft (TEC-4047).
	const detailsComplete = areDetailsComplete(details, portalId, amount, campaignCurrency);

	useEffect(() => {
		if (!hasHydrated) return;
		if (!amount) navigate({ to: `/$portalId/$campaignId/donate`, params: { portalId, campaignId } });
		else if (!detailsComplete)
			navigate({ to: `/$portalId/$campaignId/donate/details`, params: { portalId, campaignId }, replace: true });
	}, [navigate, portalId, campaignId, hasHydrated, amount, detailsComplete]);

	// See docs/notes/portals/donate-checkout.md: the Apple Pay merchant-validation listener (TEC-3637).
	useEffect(() => {
		// Before the Drop-in exists: this effect runs ahead of the Adyen init below. A guard that fails only
		// loses the better report, never the checkout, so its failure is swallowed.
		try {
			installApplePaySessionGuard();
		} catch (error) {
			console.warn('Apple Pay session guard not installed:', error);
		}
		const handleApplePayValidationFailure = (event: PromiseRejectionEvent) => {
			if (isVersionSkewReloadInProgress()) return;
			const errorInfo = extractErrorInfo(event.reason);
			if (!isApplePaySessionAbortError(errorInfo.name, errorInfo.message, errorInfo.stack)) return;

			toast.error(t(errorMessages.applePayUnavailable), { duration: 15_000 });
			// Swallowed: a rejection here would re-enter `unhandledrejection` and be ticketed as a second error.
			logError({
				data: {
					route: window.location.pathname,
					name: '[ADYEN CHECKOUT] applePayMerchantValidation: ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			}).catch((logErr) => {
				console.error('Failed to log error to backend:', logErr);
			});
		};
		window.addEventListener('unhandledrejection', handleApplePayValidationFailure);
		// The global handler skips the Apple Pay signature only while this listener is there to report it.
		setApplePayReporterMounted(true);

		return () => {
			window.removeEventListener('unhandledrejection', handleApplePayValidationFailure);
			setApplePayReporterMounted(false);
		};
	}, [t, logError]);

	useEffect(() => {
		console.time('Adyen Checkout initialization');

		// Up to four round-trips before mount: a donor pressing back left `.mount` a null ref (TEC-3694).
		let cancelled = false;
		let dropin: Dropin | undefined;

		// `details.country` is empty without a certificate and Adyen will not init without one. Kept out of
		// `preregisterDonation`: an invented country there would reach the fiscal certificate.
		const adyenCountryCode = details.country || DEFAULT_DONOR_COUNTRY;

		const initAdyenCheckout = async () => {
			if (!detailsComplete) return;
			if (!currencyCode) {
				toast.error(t(errorMessages.internal));
				return;
			}

			const mintPreregistration = () =>
				generateCaptchaTokenResult().then(({ data: captchaToken }) => {
					if (!captchaToken || !amount) return;

					return preregisterDonation({
						certificated: details.certificate,
						amount: amount,
						campaignId,
						currency: currencyCode,
						applicationId: {
							type: applicationType.portal,
							host: portalId,
						},
						donor: {
							name: details.firstname,
							lastName: details.lastname,
							contact: {
								email: details.email,
								language: localeToLanguage[language],
								physicalAddress: {
									region: details.region,
									street: {
										name: details.streetName,
										number: details.streetNumber,
										type: '',
									},
									additionalInfo: details.additionalInfo,
									city: details.city,
									country: details.country,
									postalCode: details.cp,
									province: details.province,
								},
							},
							document: {
								number: details.documentNumber,
								type: details.documentType,
							},
							subscribeNewsletter: false, // TODO: we should ask for this?
						},
						checkbox: [
							termsContent && { type: 'termsAndConditions' as const, ...termsContent },
							ngosTermsContent && { type: 'ngoTermsAndConditions' as const, ...ngosTermsContent },
							telefonicaTermsContents && {
								type: 'telefonicaTermsAndConditions' as const,
								...telefonicaTermsContents,
							},
						].filter((value) => value !== undefined),
						captchaToken,
					});
				});

			// The no-op `catch` marks it handled: `onSubmit` awaits it and reports a failure when the donor taps pay,
			// so a donor who never does leaves nothing to ticket (TEC-4048).
			const mintHandledPreregistration = () => {
				const preregistration = mintPreregistration();
				preregistration.catch(() => {});
				return preregistration;
			};

			// Read at submit time, not captured: a refused token is replaced below for the donor's next tap.
			const cachedPreregistration = getCachedPreregistration();
			let preloadPreregisterDonation = cachedPreregistration?.captchaPassed
				? Promise.resolve(cachedPreregistration)
				: mintHandledPreregistration();

			// Card and wallets hand our own rejection to `onPaymentFailed` in different shapes (TEC-4115), so it is marked here.
			let rejectedByCheckout = false;
			const rejectAnswered = (actions: { reject: () => void }) => {
				rejectedByCheckout = true;
				actions.reject();
			};

			if (!amount) return;

			const cached = getCachedPaymentMethods();
			const paymentMethods = await (async () => {
				if (cached && (cached as { captchaPassed?: boolean }).captchaPassed) return cached;
				const { data: captchaToken } = await generateCaptchaTokenResult();
				if (!captchaToken) return undefined;
				return trpcClient.payments.getPaymentMethods.mutate({
					amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
					countryCode: adyenCountryCode,
					locale: language,
					captchaToken,
				});
			})();

			if (cancelled) return;

			if (!(paymentMethods as { captchaPassed?: boolean } | undefined)?.captchaPassed) {
				toast.error(t(errorMessages.captchaFailed));
				return;
			}

			// Safe to retry: the SDK init only fetches config, with no donation side effects.
			const checkout = await retry({ attempts: 3, delayInMs: 300 }, () => {
				// Not a throw: `retry` would sit through two backoffs before rethrowing it.
				if (cancelled) return undefined;
				return AdyenCheckout({
					locale: ADYEN_LOCALE_BY_LANGUAGE.get(language) ?? DEFAULT_ADYEN_LOCALE,
					countryCode: adyenCountryCode,
					environment: getRuntimeAdyenClientKey().startsWith('test_') ? 'test' : 'live',
					clientKey: getRuntimeAdyenClientKey(),
					paymentMethodsResponse: paymentMethods as PaymentMethodsResponse,
					amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
					onSubmit: async (state, _component, actions) => {
						try {
							const preregisterDonationResult = await preloadPreregisterDonation;

							if (!preregisterDonationResult?.captchaPassed) {
								toast.error(t(errorMessages.captchaFailed));
								rejectAnswered(actions);
								return;
							}

							const { donationId, paymentToken } = preregisterDonationResult;

							// No captcha: Apple Pay's 30s authorization window can't spare it. See docs/notes/payment-checkout.md
							const result = await processPayment({
								donationId,
								amount: {
									currency: currencyCode,
									value: amountToMinorUnits(amount, currencyCode),
								},
								paymentMethodData: state.data,
								returnUrl: window.location.origin,
								paymentToken,
							});

							const { resultCode, action, order, donationToken } = result;

							// Adyen requires this even when the payment is unsuccessful.
							actions.resolve({
								resultCode: resultCode as ResultCode,
								action: action as PaymentAction,
								order: order as Order,
								donationToken,
							});
						} catch (error) {
							// A refused payment token (DR-13): docs/notes/portals/donate-checkout.md
							if (isClientFaultRpcError(error)) {
								clearCheckoutCache();
								preloadPreregisterDonation = mintHandledPreregistration();
								toast.error(t(errorMessages.paymentSessionExpired), { duration: 15_000 });
								rejectAnswered(actions);
								return;
							}
							// Adyen refused the card the donor typed (TEC-4113): answered and not charged, as in DR-15.
							if (isRejectedPaymentDetailsRpcError(error)) {
								clearCheckoutCache();
								preloadPreregisterDonation = mintHandledPreregistration();
								setPaymentFailed(true);
								rejectAnswered(actions);
								return;
							}
							console.error('onSubmit', error);
							const errorInfo = extractErrorInfo(error);
							logError({
								data: {
									route: window.location.pathname,
									name: '[ADYEN CHECKOUT] onSubmit: ' + errorInfo.name,
									message: errorInfo.message,
									stack: errorInfo.stack,
								},
							});
							toast.error(t(errorMessages.internal), { duration: 15_000 });
							rejectAnswered(actions);
						}
					},
					onAdditionalDetails: async (state, _component, actions) => {
						try {
							const { data: captchaToken } = await generateCaptchaTokenResult();
							if (!captchaToken) {
								toast.error(t(errorMessages.captchaFailed));
								rejectAnswered(actions);
								return;
							}

							const result = await getPaymentDetails({
								paymentDetailsData: state.data,
								captchaToken,
							});

							if (!result.captchaPassed) {
								toast.error(t(errorMessages.captchaFailed));
								rejectAnswered(actions);
								return;
							}

							const { resultCode, action, order, donationToken } = result;

							// Adyen requires this even when the payment is unsuccessful.
							actions.resolve({
								resultCode: resultCode as ResultCode,
								action: action as PaymentAction,
								order: order as Order,
								donationToken,
							});
						} catch (error) {
							// As in `onSubmit`, minus the re-mint: this step carries no payment token.
							if (isClientFaultRpcError(error)) {
								toast.error(t(errorMessages.paymentSessionExpired), { duration: 15_000 });
								rejectAnswered(actions);
								return;
							}
							console.error('onAdditionalDetails', error);
							const errorInfo = extractErrorInfo(error);
							logError({
								data: {
									route: window.location.pathname,
									name: '[ADYEN CHECKOUT] onAdditionalDetails: ' + errorInfo.name,
									message: errorInfo.message,
									stack: errorInfo.stack,
								},
							});
							toast.error(t(errorMessages.internal), { duration: 15_000 });
							rejectAnswered(actions);
						}
					},
					onPaymentCompleted: async (result, component) => {
						const preregisterDonationResult = await preloadPreregisterDonation;
						if (!preregisterDonationResult?.captchaPassed) {
							toast.error(t(errorMessages.captchaFailed));
							return;
						}

						console.info(result, component);

						if (preregisterDonationResult?.donationId) {
							setDonationToConfirm({ id: preregisterDonationResult.donationId, campaignId });
						}

						navigate({ to: '/$portalId/$campaignId/donate/thanks', params: { portalId, campaignId } });
					},
					onPaymentFailed: (result, component) => {
						console.info(result, component);
						// Our own rejection: its catch already told the donor, and reported if it should (TEC-4115).
						if (rejectedByCheckout) {
							rejectedByCheckout = false;
							component?.setStatus('ready');
							return;
						}
						const resultCode = (result as { resultCode?: string } | undefined)?.resultCode;

						// A delivered failed final spends the reference/token pair, so a retry needs a new one (DR-15); a dropped
						// response keeps its token, so the retry collapses into any charge that did happen (DR-10).
						if (resultCode === 'Refused' || resultCode === 'Cancelled' || resultCode === 'Error') {
							// Prepared now so "Try again" is instant. `setStatus` must run here: Adyen's `handleFailedResult` sets
							// `error` just before this callback, so only a later reset holds.
							clearCheckoutCache();
							preloadPreregisterDonation = mintHandledPreregistration();
							component?.setStatus('ready');
							setPaymentFailed(true);
							// Not reported (DR-16): "not charged" is an answer, and `{"resultCode":"Error"}` kept reopening TEC-3663.
							return;
						}

						logError({
							data: {
								route: window.location.pathname,
								name: '[ADYEN CHECKOUT] onPaymentFailed',
								message: JSON.stringify(result),
								stack: undefined,
							},
						});
					},
					onError: (error, component) => {
						// `dropin.unmount()` makes Adyen abandon in-flight work and report it here; a lost run has no donor.
						if (cancelled) return;

						const errorInfo = extractErrorInfo(error);
						// A donor closing a wallet sheet arrives as type `CANCEL` on `name`, for Google Pay and Apple Pay (TEC-3652).
						if (errorInfo.name === 'CANCEL') return;

						// Our copy: Adyen's message is an untranslated developer string (TEC-3702); it still goes in the report.
						const copy = isApplePaySessionFailure(errorInfo.message)
							? errorMessages.applePayUnavailable
							: errorMessages.internal;
						toast.error(t(copy), { duration: 15_000 });
						console.error(errorInfo.name, errorInfo.message, errorInfo.stack, component);
						logError({
							data: {
								route: window.location.pathname,
								name: '[ADYEN CHECKOUT] onError: ' + errorInfo.name,
								message: errorInfo.message,
								stack: errorInfo.stack,
								// What actually failed: Adyen wraps an Apple Pay session failure as this error's cause.
								cause: errorInfo.cause,
							},
						});
					},
				});
			});
			if (!checkout) return;

			// Adyen's `isAvailable()` decides whether Apple Pay renders, never the viewport: docs/notes/portals/donate-checkout.md
			const paymentMethodComponents: DropinConfiguration['paymentMethodComponents'] = [ApplePay];

			if (ENABLED_GOOGLE_PAY_PORTALS.has(portalId)) {
				paymentMethodComponents.push(GooglePay);
			}

			paymentMethodComponents.push(Card, CashAppPay);

			const dropinContainer = dropinContainerRef.current;
			if (cancelled || !dropinContainer) return;

			dropin = new Dropin(checkout, {
				paymentMethodComponents,
				// Wallet buttons above the list, so the card form is not one click away behind a chooser.
				instantPaymentTypes: ['applepay', 'googlepay'],
				// Not `openFirstPaymentMethod`, which follows /paymentMethods order. It outranks stored cards, which this
				// anonymous checkout never renders; the signed-in donors app does, so it omits this.
				openPaymentMethod: { type: 'scheme' },
				paymentMethodsConfiguration: {
					card: {
						hasHolderName: true,
						holderNameRequired: true,
						enableStoreDetails: true,
						hideCVC: false,
					},
				},
			});
			dropin.mount(dropinContainer);

			console.timeEnd('Adyen Checkout initialization');
		};

		initAdyenCheckout().catch((error) => {
			if (cancelled) return;

			const errorInfo = extractErrorInfo(error);
			toast.error(t(errorMessages.internal));
			logError({
				data: {
					route: window.location.pathname,
					name: '[ADYEN CHECKOUT] init: ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			});
		});

		return () => {
			cancelled = true;
			// Otherwise a re-run leaves a zombie checkout live on a node React has dropped.
			dropin?.unmount();
		};
	}, [
		amount,
		campaignId,
		currencyCode,
		detailsComplete,
		details.additionalInfo,
		details.certificate,
		details.city,
		details.country,
		details.cp,
		details.documentNumber,
		details.documentType,
		details.email,
		details.firstname,
		details.lastname,
		details.province,
		details.region,
		details.streetName,
		details.streetNumber,
		getPaymentDetails,
		language,
		navigate,
		ngosTermsContent,
		preregisterDonation,
		processPayment,
		queryClient,
		portalId,
		telefonicaTermsContents,
		termsContent,
		setDonationToConfirm,
		getCachedPreregistration,
		getCachedPaymentMethods,
		clearCheckoutCache,
		t,
		logError,
	]);

	return (
		<>
			<Title size="lg" className="text-center">
				{t`Payment method`}
			</Title>

			<DivWithPrimaryColorVariable className="min-h-107">
				{paymentFailed && (
					/* `min-h-107` repeats the container's, so the panel centres in the box the Drop-in would fill. */
					<div className="flex min-h-107 flex-col items-center justify-center gap-6 px-4 text-center">
						<div className="bg-destructive/10 flex size-12 items-center justify-center rounded-full">
							<CircleAlertIcon className="text-destructive size-6" />
						</div>
						<p className="max-w-md">{t(errorMessages.paymentNotCompleted)}</p>
						<Button size="2xl" className="w-full min-w-fit lg:w-60" onClick={() => setPaymentFailed(false)}>
							{t`Try again`}
						</Button>
					</div>
				)}
				{/* Stays mounted under the panel: the reset Drop-in is what "Try again" reveals. */}
				<div ref={dropinContainerRef} className={paymentFailed ? 'hidden' : undefined} />
			</DivWithPrimaryColorVariable>

			<p className="text-muted-foreground/50 mx-auto mt-auto flex max-w-md gap-2 text-center text-sm leading-none font-medium whitespace-pre-line">
				{t`protected by reCAPTCHA.`}
				<a href="https://policies.google.com/privacy" className="underline" target="_blank" rel="noreferrer noopener">
					{t`Privacy`}
				</a>
				<a href="https://policies.google.com/terms" className="underline" target="_blank" rel="noreferrer noopener">
					{t`Terms`}
				</a>
			</p>
		</>
	);
}
